import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

import { sdsColors, sdsRadius, SDS_RAILS } from '../theme/solutionsDesignSystem';
import pbdqRoutes from '../pbdqRoutes';

const useStyles = makeStyles(() => ({
    wrapper: {
        marginTop: 56,
    },
    card: {
        border: '1px solid #E6E9EE',
        borderRadius: 24,
        display: 'flex',
        flexWrap: 'wrap',
        background: '#F8F9FB',
        overflow: 'hidden',
        minHeight: 560,
    },
    textCol: {
        flex: '1 1 420px',
        minWidth: 0,
        padding: 'clamp(28px, 4vw, 48px)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: 24,
        boxSizing: 'border-box',
    },
    textInner: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 16,
    },
    topRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        flexWrap: 'wrap',
        minHeight: 64,
    },
    logoBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        background: '#FFFFFF',
        border: '1px solid #E6E9EE',
        borderRadius: 12,
        padding: '10px 16px',
        height: 64,
        boxSizing: 'border-box',
    },
    orgLogo: {
        height: 40,
        width: 'auto',
        maxWidth: 200,
        objectFit: 'contain',
        display: 'block',
    },
    tag: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '6px 12px',
        borderRadius: 999,
        background: '#EEF3F8',
        color: '#2A4057',
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        fontFamily: "'Figtree', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    title: {
        fontSize: 'clamp(22px, 2.2vw, 30px)',
        lineHeight: 1.25,
        letterSpacing: '-0.02em',
        fontWeight: 700,
        margin: 0,
        maxWidth: '32ch',
        minHeight: '3.75em',
        color: '#1B2430',
        fontFamily: "'Figtree', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    body: {
        fontSize: 15.5,
        lineHeight: 1.6,
        margin: 0,
        color: '#5B6470',
        maxWidth: '66ch',
        minHeight: '6.4em',
        fontFamily: "'Figtree', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    footerRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 14,
        flexWrap: 'wrap',
        paddingTop: 8,
        marginTop: 'auto',
    },
    dots: {
        display: 'flex',
        gap: 6,
        alignItems: 'center',
    },
    dot: {
        width: 8,
        height: 8,
        borderRadius: 999,
        border: 0,
        padding: 0,
        cursor: 'pointer',
        background: '#D5D9E0',
        transition: 'width 300ms cubic-bezier(.32,.72,0,1), background 300ms cubic-bezier(.32,.72,0,1)',
    },
    dotActive: {
        width: 24,
        background: '#ED6628',
    },
    arrows: {
        display: 'flex',
        gap: 8,
    },
    arrowBtn: {
        width: 44,
        height: 44,
        borderRadius: 999,
        border: '1px solid #D5D9E0',
        background: '#FFFFFF',
        cursor: 'pointer',
        color: '#1B2430',
        fontSize: 16,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'border-color 150ms ease, background-color 150ms ease',
        '&:hover': {
            borderColor: '#1B2430',
            background: '#F8F9FA',
        },
    },
    imageWrapper: {
        flex: '1 1 360px',
        minWidth: 0,
        minHeight: 320,
        padding: 'clamp(20px, 3vw, 40px)',
        background: '#F0F2F5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    imageContainer: {
        width: '100%',
        maxWidth: 520,
        height: 380,
        background: '#D9DFE7',
        border: '1px solid #CFD6DF',
        borderRadius: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
    },
    imageLink: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        textDecoration: 'none',
        cursor: 'pointer',
    },
    image: {
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        display: 'block',
    },
    seeAllWrapper: {
        display: 'flex',
        justifyContent: 'flex-start',
        marginTop: 20,
    },
    seeAllLink: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 15,
        fontWeight: 700,
        textDecoration: 'none',
        color: '#A63C0C',
        whiteSpace: 'nowrap',
        fontFamily: "'Figtree', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        '&:hover': {
            textDecoration: 'underline',
        },
    },
}));

const AUTOPLAY_MS = 7000;

const PbdqStoryCarousel = ({ stories }) => {
    const classes = useStyles();
    const [index, setIndex] = useState(0);

    useEffect(() => {
        const timer = setInterval(() => {
            setIndex((i) => (i + 1) % stories.length);
        }, AUTOPLAY_MS);
        return () => clearInterval(timer);
    }, [stories.length]);

    const story = stories[index];
    const goTo = (i) => setIndex((i + stories.length) % stories.length);

    return (
        <div className={classes.wrapper}>
            <div className={classes.card}>
                <div className={classes.textCol}>
                    <div className={classes.textInner}>
                        <div className={classes.topRow}>
                            <div className={classes.logoBadge}>
                                <img
                                    src={story.logo}
                                    alt={`${story.orgName} logo`}
                                    className={classes.orgLogo}
                                    style={story.logoScale ? {
                                        transform: `scale(${story.logoScale})`,
                                        transformOrigin: 'center center',
                                    } : undefined}
                                />
                            </div>
                            <span className={classes.tag}>{story.tag}</span>
                        </div>
                        <h3 className={classes.title}>{story.title}</h3>
                        <p className={classes.body}>{story.body}</p>
                    </div>
                    <div className={classes.footerRow}>
                        <div className={classes.dots}>
                            {stories.map((s, i) => (
                                <button
                                    key={s.orgName}
                                    type="button"
                                    aria-label={`Story ${i + 1}`}
                                    onClick={() => goTo(i)}
                                    className={clsx(classes.dot, i === index && classes.dotActive)}
                                />
                            ))}
                        </div>
                        <div className={classes.arrows}>
                            <button
                                type="button"
                                aria-label="Previous story"
                                onClick={() => goTo(index - 1)}
                                className={classes.arrowBtn}
                            >
                                ←
                            </button>
                            <button
                                type="button"
                                aria-label="Next story"
                                onClick={() => goTo(index + 1)}
                                className={classes.arrowBtn}
                            >
                                →
                            </button>
                        </div>
                    </div>
                </div>
                <div className={classes.imageWrapper}>
                    <div className={classes.imageContainer}>
                        {story.link ? (
                            <a
                                href={story.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={classes.imageLink}
                                aria-label={`Read story: ${story.orgName} — ${story.title}`}
                            >
                                <img src={story.image} alt={story.orgName} className={classes.image} />
                            </a>
                        ) : (
                            <img src={story.image} alt={story.orgName} className={classes.image} />
                        )}
                    </div>
                </div>
            </div>
            <div className={classes.seeAllWrapper}>
                <Link to={pbdqRoutes.impact} className={classes.seeAllLink}>
                    See all stories →
                </Link>
            </div>
        </div>
    );
};

export default PbdqStoryCarousel;

